import {
    VcArrayClaim,
    VcClaim,
    VcClaimTitle,
    VcContext,
    VcNotEmptyClaim,
    VcSchema,
    VcStringEnumClaim,
} from '../../decorators';
import { LinkedCredential, LinkedFile, Rfc3339Date, Rfc3339DateTime } from '../../models';

export const VOCAB_NAMESPACE = 'https://www.w3.org/ns/credentials/issuer-dependent';

@VcContext({ name: 'Foo', namespace: VOCAB_NAMESPACE })
export class FooVcContext {
    @VcNotEmptyClaim
    foo!: string;
}

@VcContext({ name: 'Bar', namespace: VOCAB_NAMESPACE })
export class BarVcContext {
    @VcNotEmptyClaim
    bar!: string;
}

@VcContext({ name: 'NestedStruct', namespace: VOCAB_NAMESPACE })
export class NestedStructVcContext {
    @VcClaim
    linkedProp?: LinkedCredential<FooVcContext>;
}

@VcContext({ name: 'Baz', namespace: VOCAB_NAMESPACE })
export class BazVcContext {
    @VcClaim
    field1?: LinkedCredential<FooVcContext>;

    @VcNotEmptyClaim
    field2!: LinkedFile;

    @VcNotEmptyClaim
    field3!: BarVcContext;

    @VcStringEnumClaim([
        { value: 'option1', title: 'Option 1' },
        { value: 'option2', title: 'Option 2' },
    ])
    @VcNotEmptyClaim
    field4!: 'option1' | 'option2';

    @VcNotEmptyClaim
    @VcArrayClaim(LinkedCredential<FooVcContext>)
    field5!: LinkedCredential<FooVcContext>[];

    @VcNotEmptyClaim
    field6!: NestedStructVcContext;

    @VcNotEmptyClaim
    field7!: Rfc3339DateTime;

    @VcNotEmptyClaim
    field8!: Rfc3339Date;

    @VcArrayClaim(String)
    field11_1?: string[];

    @VcArrayClaim(String)
    @VcClaim
    field11_2?: string[];

    @VcClaim
    @VcArrayClaim(String)
    field11_3?: string[];

    @VcArrayClaim(BarVcContext)
    field12_1?: BarVcContext[];

    @VcArrayClaim(BarVcContext)
    @VcClaim
    field12_2?: BarVcContext[];

    @VcClaim
    @VcArrayClaim(BarVcContext)
    field12_3?: BarVcContext[];
}

@VcContext({ name: 'Qux', namespace: VOCAB_NAMESPACE, vcType: ['BazCredential'] })
export class QuxVcContext {
    @VcNotEmptyClaim
    field1_1!: LinkedCredential<FooVcContext>;

    @VcNotEmptyClaim
    field1_2!: LinkedCredential<FooVcContext>;

    @VcClaim
    field1_3?: LinkedCredential<FooVcContext>;

    @VcClaim
    field1_4?: LinkedCredential<FooVcContext>;

    @VcNotEmptyClaim
    field2_1!: LinkedFile;

    @VcNotEmptyClaim
    field2_2!: LinkedFile;

    @VcStringEnumClaim([
        { value: 'option1', title: 'Option 1' },
        { value: 'option2', title: 'Option 2' },
    ])
    @VcNotEmptyClaim
    field3_1!: 'option1' | 'option2';

    @VcNotEmptyClaim
    @VcStringEnumClaim([
        { value: 'option1', title: 'Option 1' },
        { value: 'option2', title: 'Option 2' },
    ])
    field3_2!: 'option1' | 'option2';

    @VcArrayClaim(Number)
    @VcNotEmptyClaim
    field4_1!: number[];

    @VcNotEmptyClaim
    @VcArrayClaim(Number)
    field4_2!: number[];

    @VcNotEmptyClaim
    @VcClaimTitle('Field title')
    field5_1!: number;

    @VcClaimTitle('Field title')
    @VcNotEmptyClaim
    field5_2!: number;

    @VcClaim
    @VcNotEmptyClaim
    field6_1!: number;

    @VcNotEmptyClaim
    @VcClaim
    field6_2!: number;

    @VcArrayClaim(Rfc3339DateTime)
    @VcNotEmptyClaim
    field7_1!: Rfc3339DateTime[];

    @VcNotEmptyClaim
    @VcArrayClaim(Rfc3339DateTime)
    field7_2!: Rfc3339DateTime[];

    @VcArrayClaim(Rfc3339Date)
    @VcNotEmptyClaim
    field8_1!: Rfc3339Date[];

    @VcNotEmptyClaim
    @VcArrayClaim(Rfc3339Date)
    field8_2!: Rfc3339Date[];

    @VcNotEmptyClaim
    field9!: BarVcContext;

    @VcArrayClaim(BarVcContext)
    @VcClaimTitle('Field 10')
    field10?: BarVcContext[];

    @VcArrayClaim(String)
    field11_1?: string[];

    @VcArrayClaim(String)
    @VcClaim
    field11_2?: string[];

    @VcClaim
    @VcArrayClaim(String)
    field11_3?: string[];
}

@VcSchema()
export class Foo {
    @VcNotEmptyClaim
    foo!: string;
}

@VcSchema()
export class Bar {
    @VcNotEmptyClaim
    bar!: string;
}

@VcSchema()
export class NestedStruct {
    @VcClaim
    linkedProp?: LinkedCredential<Foo>;
}

@VcSchema()
export class Baz {
    @VcClaim
    field1?: LinkedCredential<Foo>;

    @VcNotEmptyClaim
    field2!: LinkedFile;

    @VcNotEmptyClaim
    field3!: Bar;

    @VcStringEnumClaim([
        { value: 'option1', title: 'Option 1' },
        { value: 'option2', title: 'Option 2' },
    ])
    @VcNotEmptyClaim
    field4!: 'option1' | 'option2';

    @VcNotEmptyClaim
    @VcArrayClaim(LinkedCredential<Foo>)
    field5!: LinkedCredential<Foo>[];

    @VcNotEmptyClaim
    field6!: NestedStruct;

    @VcNotEmptyClaim
    field7!: Rfc3339DateTime;

    @VcNotEmptyClaim
    field8!: Rfc3339Date;

    @VcArrayClaim(String)
    field11_1?: string[];

    @VcArrayClaim(String)
    @VcClaim
    field11_2?: string[];

    @VcClaim
    @VcArrayClaim(String)
    field11_3?: string[];

    @VcArrayClaim(Bar)
    field12_1?: Bar[];

    @VcArrayClaim(Bar)
    @VcClaim
    field12_2?: Bar[];

    @VcClaim
    @VcArrayClaim(Bar)
    field12_3?: Bar[];
}

@VcSchema()
export class Qux {
    @VcNotEmptyClaim
    field1_1!: LinkedCredential<Foo>;

    @VcNotEmptyClaim
    field1_2!: LinkedCredential<Foo>;

    @VcClaim
    field1_3?: LinkedCredential<Foo>;

    @VcClaim
    field1_4?: LinkedCredential<Foo>;

    @VcNotEmptyClaim
    field2_1!: LinkedFile;

    @VcNotEmptyClaim
    field2_2!: LinkedFile;

    @VcStringEnumClaim([
        { value: 'option1', title: 'Option 1' },
        { value: 'option2', title: 'Option 2' },
    ])
    @VcNotEmptyClaim
    field3_1!: 'option1' | 'option2';

    @VcNotEmptyClaim
    @VcStringEnumClaim([
        { value: 'option1', title: 'Option 1' },
        { value: 'option2', title: 'Option 2' },
    ])
    field3_2!: 'option1' | 'option2';

    @VcArrayClaim(Number)
    @VcNotEmptyClaim
    field4_1!: number[];

    @VcNotEmptyClaim
    @VcArrayClaim(Number)
    field4_2!: number[];

    @VcNotEmptyClaim
    @VcClaimTitle('Field title')
    field5_1!: number;

    @VcClaimTitle('Field title')
    @VcNotEmptyClaim
    field5_2!: number;

    @VcClaim
    @VcNotEmptyClaim
    field6_1!: number;

    @VcNotEmptyClaim
    @VcClaim
    field6_2!: number;

    @VcArrayClaim(Rfc3339DateTime)
    @VcNotEmptyClaim
    field7_1!: Rfc3339DateTime[];

    @VcNotEmptyClaim
    @VcArrayClaim(Rfc3339DateTime)
    field7_2!: Rfc3339DateTime[];

    @VcArrayClaim(Rfc3339Date)
    @VcNotEmptyClaim
    field8_1!: Rfc3339Date[];

    @VcNotEmptyClaim
    @VcArrayClaim(Rfc3339Date)
    field8_2!: Rfc3339Date[];

    @VcNotEmptyClaim
    field9!: Bar;

    @VcArrayClaim(Bar)
    @VcClaimTitle('Field 10')
    field10?: Bar[];

    @VcArrayClaim(String)
    field11_1?: string[];

    @VcArrayClaim(String)
    @VcClaim
    field11_2?: string[];

    @VcClaim
    @VcArrayClaim(String)
    field11_3?: string[];
}
